import { Success } from '@icons/Success';
import { Formik } from 'formik';
import { useSlug } from 'hooks/useSlug';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import * as yup from 'yup';
import { Accordion } from '../../components/Accordion';
import { Grid, GridItem, GridRow } from '../../components/Grid';
import { componentMap, initialValueMap } from './componentMap';
import { CombinedInputs } from './inputs/CombinedInputs';
import { SubmitButton } from './inputs/SubmitButton';
import {
  AccordionContainer,
  ErrorMessage,
  SuccessDescription,
  SuccessMessage,
  SuccessWrapper,
} from './styles';
import { validationSchemaMap } from './validation';

import { FileUploader } from 'components/Forms/DynamicForm/components/Inputs/FileUploader';
import type { EvalancheFormComponent } from 'server/repository/hygraph/generated/graphqlTypes';
import type { ISharedTemplateProps } from 'templates/TemplateMapper/types';
import type { CombinedType, FieldType, Form, ReArrangedField } from '../../types/EvalancheTypes';

//  Evalanche Form container component.
// Sets up a form with Formik, validations, success and error states, and submission handling.
// Pass in a form built in Evalanche and identified in the CMS.

const customFields = ['form_EMAIL', 'form_EMAIL_CONFIRMATION'];

const hiddenFields = ['form_IMAGE', 'form_HIDDEN'];

interface IEvalancheForm extends ISharedTemplateProps {
  templateProps: EvalancheFormComponent;
}

export const EvalancheForm = ({
  templateProps: {
    evalancheHost: host,
    formId,
    accordionTitle = '',
    fieldsBeforeAccordion = 1,
    fieldsAfterAccordion = 1,
    collapsed = true,
    formFileUploader: fileUploadField,
    successMessage,
    successMessageDescription,
    market,
  },
  formSubmitCallback = () => {},
}: IEvalancheForm) => {
  const slug = useSlug();
  const [formSuccess, setFormSuccess] = useState<boolean>(false);
  const [formFailed, setFormFailed] = useState<boolean>(false);
  const [formData, setFormData] = useState<Form | undefined>();
  const [formError, setFormError] = useState<string>('');
  const [reArrangedFields, setRearrangedFields] = useState<ReArrangedField[]>();
  const [accordionFields, setAccordionFields] = useState<ReArrangedField[]>();
  const [hangingFields, setHangingFields] = useState<ReArrangedField[]>();
  const successMessageRef = useRef<HTMLDivElement>(null);

  const arrangeFields = (rawData: Form) => {
    const mappedFields: any[] = [];
    if (rawData && rawData.fields) {
      for (let i = 0; i < rawData.fields.length || 0; i += 1) {
        if (rawData.fields[i]?.widget_id === 23) {
          // eslint-disable-next-line no-param-reassign
          rawData.fields[i].type = 'select';
        }
        if (
          rawData.fields[i]?.sort === rawData.fields[i + 1]?.sort &&
          !mappedFields.some((field) => field?.sort === rawData?.fields?.[i]?.sort)
        ) {
          mappedFields.push({
            type: 'combinedFields',
            sort: rawData.fields?.[i]?.sort,
            fields: [rawData?.fields?.[i]],
          });
        } else if (
          i > 0 &&
          rawData?.fields?.[i]?.sort === rawData?.fields?.[i - 1]?.sort &&
          mappedFields.some((field) => field?.sort === rawData?.fields?.[i]?.sort)
        ) {
          const index = mappedFields.findIndex(
            (field) => field?.sort === rawData?.fields?.[i].sort,
          );
          mappedFields[index].fields.push(rawData?.fields?.[i]);
        } else {
          mappedFields.push(rawData?.fields?.[i]);
        }
      }
    }
    if (fieldsBeforeAccordion) {
      setRearrangedFields(mappedFields.slice(0, fieldsBeforeAccordion));
      if (!fieldsAfterAccordion) {
        setAccordionFields(mappedFields.slice(fieldsBeforeAccordion));
      } else {
        setAccordionFields(
          mappedFields.slice(fieldsBeforeAccordion, mappedFields.length - fieldsAfterAccordion),
        );
        setHangingFields(mappedFields.slice(mappedFields.length - fieldsAfterAccordion));
      }
    } else {
      setRearrangedFields(mappedFields);
    }
  };

  const getForm = async (submit?: boolean): Promise<any> =>
    fetch(`https://${host}/api/form/v1/${formId}`, {
      method: 'GET',
      headers: { Origin: `https://${host}` },
    })
      .then(async (response) => {
        const rawData = await response?.json();

        setFormData(rawData);
        if (!submit) {
          arrangeFields(rawData);
        }
        return rawData;
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error('Something went wrong', err);
      });

  useEffect(() => {
    getForm();
    return () => {};
  }, []);

  useEffect(() => {
    if (formSuccess) {
      if (successMessageRef.current) {
        successMessageRef.current.scrollIntoView();
      }
    }
  }, [formSuccess]);

  const handleError = async (error: any, actions: any, values: any) => {
    // TODO: handle different error codes and object when they become apparent,
    // I was not able to find a way to trigger them.
    const errorMessages = {} as Record<string, any>;
    if (error.code === 106) {
      error.mandatory_error_attributes?.forEach((attribute: string) => {
        errorMessages[attribute] = formData?.fields?.find(
          (field: FieldType) => field.name === attribute,
        )?.translation?.validation_error;
      });
      actions.setErrors(errorMessages);
      setFormError(formData?.form?.translation?.missing_validation || '');
    } else if (error.code === 102) {
      error.validation_error_attributes?.forEach((attribute: string) => {
        errorMessages[attribute] = formData?.fields?.find(
          (field: FieldType) => field.name === attribute,
        )?.translation?.validation_error;
      });
      actions.setErrors(errorMessages);
      setFormError(formData?.form?.translation?.failed_validation || '');
    } else if (error.code === 107) {
      // this should never happen since we are retrieving a token before submitting.
      // However, for completeness =>
      const newData = await getForm(true);
      // not best solution, but not to force the user to submit recalling submission.
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      handleSubmit(values, actions, true, newData);
    }
    setFormFailed(true);
  };

  const formSubmit = async (
    evalancheData: any,
    values: any,
    actions: any,
    retry?: boolean,
    newData?: Form,
  ) => {
    setFormFailed(false);
    fetch(`https://${host}/api/form/v1/${formId}`, {
      method: 'POST',
      body: JSON.stringify({
        token: retry ? newData?.token : formData?.token,
        profile_uid: 'zero', // TODO: needs more investigation in different forms types
        data: evalancheData,
        options: {
          force_new_profile: false,
          allow_empty_profile: true,
        },
      }),
      headers: { Origin: `https://${host}` },
    })
      .then(async (response) => {
        const data = await response?.json();
        // error code 104 is duplicate email,
        // we shouldnt show error messages in this case
        if (data?.error && data?.error?.code !== 104) {
          if (!retry) {
            handleError(data.error, actions, values);
          }
          setFormSuccess(false);
        } else {
          formSubmitCallback();
          setFormSuccess(true);
        }
      })
      .catch(() => {
        setFormSuccess(false);
        setFormFailed(true);
      })
      .finally(() => {
        actions.setSubmitting(false);
      });
  };

  const handleSubmit = async (values: any, actions: any, retry?: boolean, newData?: Form) => {
    if (values.couponFile) {
      fetch('/next-api/promotion/file', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ couponFile: values.couponFile, market: market, slug: slug }),
        redirect: 'follow',
      }).then(async (res) => {
        const result = await res?.json();
        const evalancheData = Object.keys(values)
          .filter((item) => item !== 'couponFile')
          .map((key) => ({ field_name: key, value: values[key] === true ? '1' : values[key] }));
        evalancheData.push({ field_name: 'form_IMAGE', value: result?.data });
        formSubmit(evalancheData, values, actions, retry, newData);
      });
    } else {
      const evalancheData = Object.keys(values).map((key) => ({
        field_name: key,
        value: values[key] === true ? '1' : values[key],
      }));
      formSubmit(evalancheData, values, actions, retry, newData);
    }
  };

  const renderAccordionFields = (fields: ReArrangedField[], formikProps: any) => [
    {
      title: accordionTitle,
      body: fields.map((field: ReArrangedField) => {
        let FieldComponent: any;
        if (field.type === 'combinedFields') {
          return (
            <CombinedInputs
              field={field as CombinedType}
              formikProps={formikProps}
              key={`combinedField-${field.sort}`}
            />
          );
        }
        if (customFields.includes(field.name!)) {
          FieldComponent = componentMap[field.name as keyof typeof componentMap];
        } else {
          FieldComponent = componentMap[field.type as keyof typeof componentMap];
        }
        if (FieldComponent) {
          return (
            <FieldComponent
              key={field.name}
              field={field as FieldType}
              formikProps={formikProps}
              isHidden={hiddenFields.includes(field.name!) || field.hidden}
            />
          );
        }

        return null;
      }),
    },
  ];

  const renderFields = (fields: ReArrangedField[], formikProps: any) =>
    fields.map((field: ReArrangedField): ReactNode | null => {
      let FieldComponent: any;
      if (field.type === 'combinedFields') {
        return (
          <CombinedInputs
            field={field as CombinedType}
            formikProps={formikProps}
            key={`combinedField-${field.sort}`}
          />
        );
      }
      if (customFields.includes(field.name!)) {
        FieldComponent = componentMap[field.name as keyof typeof componentMap];
      } else {
        FieldComponent = componentMap[field.type as keyof typeof componentMap];
      }
      if (FieldComponent) {
        return (
          <FieldComponent
            key={field.name}
            field={field as FieldType}
            formikProps={formikProps}
            isHidden={hiddenFields.includes(field.name!) || field.hidden}
          />
        );
      }

      return null;
    });

  if (formData) {
    const initialValues = initialValueMap(formData.fields);
    const validationSchema = validationSchemaMap(formData.fields);
    return (
      <Grid>
        <GridRow>
          <GridItem>
            {!formSuccess && (
              <Formik
                initialValues={initialValues}
                validationSchema={yup.object().shape(validationSchema)}
                onSubmit={handleSubmit}
              >
                {(formikProps) => (
                  <form
                    onSubmit={formikProps.handleSubmit}
                    data-test={formData?.form?.title}
                    className="flex flex-col items-center"
                  >
                    {reArrangedFields && renderFields(reArrangedFields, formikProps)}
                    {accordionFields && (
                      <AccordionContainer className="py-4 sm:w-full md:w-1/2 form-fields">
                        <Accordion
                          name={accordionTitle || ''}
                          collapsed={collapsed as boolean}
                          data={renderAccordionFields(accordionFields, formikProps)}
                        />
                      </AccordionContainer>
                    )}
                    {hangingFields && renderFields(hangingFields, formikProps)}
                    {fileUploadField && (
                      <FileUploader field={fileUploadField} formikProps={formikProps} count={1} />
                    )}
                    {formData?.form && (
                      <SubmitButton form={formData.form} formikProps={formikProps} />
                    )}
                  </form>
                )}
              </Formik>
            )}
            {formSuccess && (
              <Grid>
                <GridRow>
                  <GridItem>
                    <SuccessWrapper id="success-message" $scroll ref={successMessageRef}>
                      <Success />
                      <SuccessMessage tag="div" type="sm">
                        {successMessage}
                      </SuccessMessage>
                      <SuccessDescription tag="div" type="md">
                        {successMessageDescription}
                      </SuccessDescription>
                    </SuccessWrapper>
                  </GridItem>
                </GridRow>
              </Grid>
            )}
            {formFailed && <ErrorMessage>{formError}</ErrorMessage>}
          </GridItem>
        </GridRow>
      </Grid>
    );
  }
  return <div />;
};
